import { useEffect, useRef, type ReactNode } from "react";
import { ChevronLeft, X } from "lucide-react";

interface ManagementModalProps {
  title: string;
  /** One line under the title; a modal that explains itself in its body leaves it out. */
  description?: string;
  onClose: () => void;
  /** On a phone, a back button in place of the title (the title stays for screen readers). */
  back?: { label: string; onClick: () => void };
  /** The body doesn't pad or scroll: its content lays itself out and scrolls where it needs to. */
  flush?: boolean;
  children: ReactNode;
}

/** A roomy workspace for site management tasks that do not belong in the menu drawer. */
export function ManagementModal({ title, description, onClose, back, flush, children }: ManagementModalProps) {
  const dialog = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    heading.current?.focus({ preventScroll: true });
    return () => previous?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      className="bw-manager-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialog}
        className="bw-manager"
        data-flush={flush || undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bw-manager-title"
        aria-describedby={description ? "bw-manager-description" : undefined}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
            return;
          }
          if (event.key !== "Tab") return;
          const focusable = [...(dialog.current?.querySelectorAll<HTMLElement>(
            "button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])",
          ) ?? [])].filter((element) => element.offsetParent !== null || element === document.activeElement);
          if (focusable.length === 0) return;
          const first = focusable[0]!;
          const last = focusable.at(-1)!;
          if (event.shiftKey && (document.activeElement === first || document.activeElement === heading.current)) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <header className="bw-manager-head" data-back={back ? true : undefined}>
          {back && (
            <button type="button" className="bw-manager-back" onClick={back.onClick}>
              <ChevronLeft size={20} aria-hidden="true" />{back.label}
            </button>
          )}
          <div>
            <h2 id="bw-manager-title" ref={heading} tabIndex={-1}>{title}</h2>
            {description && <p id="bw-manager-description">{description}</p>}
          </div>
          <button type="button" className="bw-btn bw-btn--icon" onClick={onClose} aria-label={`Close ${title}`}>
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <div className="bw-manager-body">{children}</div>
      </div>
    </div>
  );
}
